import React, { useCallback, useEffect, useRef, useState } from "react";
import { useGet, api, usePost } from "../../api";
import {
  IKindredCouponsResponse,
  IKindredBrandReference,
  KindredPublishStatus,
  KindredCouponCoverage,
  IBulkCouponImportStageResponse,
  IBulkCouponImportConfirmResult,
  IBulkCouponImportValidatedRow,
  CouponBulkUpdateOperation,
  ICouponBulkUpdateRunSummary,
  PermissionAction,
  PermissionResource,
} from "../../interfaces";
import {
  Button,
  Spinner,
  Form,
  Row,
  Col,
  Badge,
  OverlayTrigger,
  Tooltip,
  Modal,
  Alert,
  Table,
} from "react-bootstrap";
import { Link, useSearchParams } from "react-router";
import Select from "react-select";
import {
  listSearchKey,
  settingsType,
  useListSearchMemory,
  useSettings,
} from "../../hooks";
import { useAuth } from "../../hooks/auth";
import { format } from "date-fns";
import { Copy, Check, Code, Download } from "react-feather";
import { BrandMultiSelect } from "../../components/workbench";
import { PageHeader, PageToolbar, ToolbarButton } from "../../components/ui";
import { NewBulkUpdateModal } from "./coupon-bulk-updates";

const publishedStatusOptions = [
  { value: "", label: "All" },
  { value: "0", label: "Draft" },
  { value: "1", label: "Awaiting Approval" },
  { value: "2", label: "Approved" },
  { value: "3", label: "Deactivated" },
];

const domainModeOptions = [
  { value: "both", label: "Both" },
  { value: "coupon_domains", label: "Coupon Domains" },
  { value: "campaign_domains", label: "Campaign Domains" },
];

const coverageOptions = [
  { value: "", label: "All" },
  { value: String(KindredCouponCoverage.None), label: "None" },
  { value: String(KindredCouponCoverage.SiteWide), label: "Site-Wide" },
  { value: String(KindredCouponCoverage.Category), label: "Category" },
  {
    value: String(KindredCouponCoverage.Product),
    label: "Product Specific",
  },
];

const statusBadge = (status: number) => {
  const map: Record<number, { bg: string; label: string }> = {
    [KindredPublishStatus.Draft]: { bg: "secondary", label: "Draft" },
    [KindredPublishStatus.AwaitingApproval]: {
      bg: "warning",
      label: "Awaiting",
    },
    [KindredPublishStatus.Approved]: { bg: "success", label: "Approved" },
    [KindredPublishStatus.Deactivated]: { bg: "danger", label: "Deactivated" },
  };
  const info = map[status] || { bg: "secondary", label: String(status) };
  return <Badge bg={info.bg}>{info.label}</Badge>;
};

const CopyButton: React.FC<{ text: string; title?: string }> = ({
  text,
  title,
}) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip>{copied ? "Copied!" : title || "Copy"}</Tooltip>}
    >
      <button
        type="button"
        className="btn btn-link btn-sm p-0 ms-1"
        style={{ lineHeight: 1, verticalAlign: "middle" }}
        onClick={handleCopy}
      >
        {copied ? <Check size={13} /> : <Copy size={13} />}
      </button>
    </OverlayTrigger>
  );
};

const COVERAGE_LABELS: Record<number, string> = {
  [KindredCouponCoverage.None]: "None",
  [KindredCouponCoverage.SiteWide]: "SiteWide",
  [KindredCouponCoverage.Category]: "Category",
  [KindredCouponCoverage.Product]: "Product",
  [KindredCouponCoverage.Manufacturer]: "Manufacturer",
};

function downloadValidatedRowsCsv(
  rows: IBulkCouponImportValidatedRow[],
  filename: string
) {
  const header =
    "id,code,brandName,currentCoverage,newCoverage,isActive,hasMetadata";
  const body = rows.map((r) =>
    [
      r.id,
      `"${r.code.replace(/"/g, '""')}"`,
      `"${r.brandName.replace(/"/g, '""')}"`,
      COVERAGE_LABELS[r.currentCoverage] ?? r.currentCoverage,
      COVERAGE_LABELS[r.coverage] ?? r.coverage,
      r.isActive ? "true" : "false",
      r.hasMetadata ? "true" : "false",
    ].join(",")
  );
  const csv = [header, ...body].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

type ImportStep = "select" | "validating" | "staged" | "confirming" | "done";

const BulkImportModal: React.FC<{ show: boolean; onHide: () => void }> = ({
  show,
  onHide,
}) => {
  const [step, setStep] = useState<ImportStep>("select");
  const [file, setFile] = useState<File | null>(null);
  const [staged, setStaged] = useState<IBulkCouponImportStageResponse | null>(
    null
  );
  const [comment, setComment] = useState("");
  const [result, setResult] = useState<IBulkCouponImportConfirmResult | null>(
    null
  );
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const stagePost = usePost<
    IBulkCouponImportStageResponse,
    { csvContent: string; filename: string }
  >();
  const confirmPost = usePost<
    IBulkCouponImportConfirmResult,
    { importId: string; comment: string }
  >();

  const handleStage = async () => {
    if (!file) return;
    setStep("validating");
    setErrorMsg(null);
    try {
      const csvContent = await file.text();
      const data = await stagePost.execute(
        "kindred/coupons/bulk-import/stage",
        { csvContent, filename: file.name }
      );
      setStaged(data);
      setStep("staged");
    } catch (err: any) {
      setErrorMsg(
        err?.data?.message || err?.message || "Failed to stage import"
      );
      setStep("select");
    }
  };

  const handleConfirm = async () => {
    if (!staged) return;
    setStep("confirming");
    setErrorMsg(null);
    try {
      const data = await confirmPost.execute(
        "kindred/coupons/bulk-import/confirm",
        { importId: staged.importId, comment }
      );
      setResult(data);
      setStep("done");
    } catch (err: any) {
      setErrorMsg(
        err?.data?.message || err?.message || "Failed to confirm import"
      );
      setStep("staged");
    }
  };

  const downloadErrorCsv = () => {
    if (!result?.errors?.length) return;
    const rows = result.errors.map(
      (e) => `${e.id},${e.coverage},"${e.error.replace(/"/g, '""')}"`
    );
    const csv = ["id,coverage,error", ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bulk-import-errors.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setStep("select");
    setFile(null);
    setStaged(null);
    setComment("");
    setResult(null);
    setErrorMsg(null);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Bulk Import — Coverage Update</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {(step === "select" || step === "validating") && (
          <>
            <p className="text-muted small mb-2">
              Upload a CSV with columns: <code>id,coverage</code>
              <br />
              Coverage accepts numeric or name: <code>0</code>/None,{" "}
              <code>1</code>/SiteWide, <code>2</code>/Category, <code>4</code>
              /Product, <code>8</code>/Manufacturer
            </p>
            <Form.Control
              type="file"
              accept=".csv"
              onChange={(e) =>
                setFile((e.target as HTMLInputElement).files?.[0] || null)
              }
            />
            {errorMsg && (
              <Alert variant="danger" className="mt-2 mb-0 py-2 small">
                {errorMsg}
              </Alert>
            )}
          </>
        )}

        {(step === "staged" || step === "confirming") && staged && (
          <>
            <div className="d-flex gap-2 mb-3 flex-wrap">
              <Badge bg="success">{staged.validCount} to update</Badge>
              <Badge bg="secondary">{staged.noChangeCount} no change</Badge>
              <Badge bg="danger">{staged.invalidCount} invalid</Badge>
              {staged.missingMetadataCount > 0 && (
                <Badge bg="warning" text="dark">
                  {staged.missingMetadataCount} without metadata
                </Badge>
              )}
            </div>

            {staged.missingMetadataCount > 0 && (
              <Alert variant="warning" className="py-2 small">
                {staged.missingMetadataCount} coupon(s) have no metadata record
                — coverage will only be updated on the coupon, not the metadata.
              </Alert>
            )}

            {staged.invalid.length > 0 && (
              <div className="mb-3">
                <div className="fw-semibold small mb-1">
                  Invalid rows ({staged.invalidCount})
                </div>
                <div
                  style={{
                    maxHeight: 150,
                    overflowY: "auto",
                    fontSize: "0.78rem",
                  }}
                >
                  <ul className="mb-0 ps-3">
                    {staged.invalid.slice(0, 30).map((r, i) => (
                      <li key={i}>
                        <code>{r.id || "(empty)"}</code>: {r.error}
                      </li>
                    ))}
                    {staged.invalid.length > 30 && (
                      <li className="text-muted">
                        … and {staged.invalid.length - 30} more
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            )}

            {staged.noChange.length > 0 && (
              <details className="mb-3">
                <summary
                  className="small fw-semibold text-muted"
                  style={{ cursor: "pointer" }}
                >
                  No change — already at target coverage ({staged.noChangeCount}
                  )
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 ms-2"
                    style={{ verticalAlign: "middle" }}
                    title="Download no-change list"
                    onClick={(e) => {
                      e.preventDefault();
                      downloadValidatedRowsCsv(
                        staged.noChange,
                        "bulk-import-no-change.csv"
                      );
                    }}
                  >
                    <Download size={13} />
                  </button>
                </summary>
                <div
                  style={{ maxHeight: 200, overflowY: "auto", marginTop: 6 }}
                >
                  <Table
                    size="sm"
                    striped
                    bordered
                    style={{ fontSize: "0.78rem" }}
                  >
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Brand</th>
                        <th>Coverage</th>
                        <th>Live</th>
                      </tr>
                    </thead>
                    <tbody>
                      {staged.noChange.map((r) => (
                        <tr key={r.id}>
                          <td>
                            <code>{r.code}</code>
                          </td>
                          <td>{r.brandName}</td>
                          <td>{COVERAGE_LABELS[r.coverage] ?? r.coverage}</td>
                          <td>
                            {r.isActive ? (
                              <Badge
                                bg="success"
                                style={{ fontSize: "0.65rem" }}
                              >
                                Live
                              </Badge>
                            ) : (
                              <Badge
                                bg="secondary"
                                style={{ fontSize: "0.65rem" }}
                              >
                                Inactive
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </details>
            )}

            {staged.valid.length > 0 && (
              <div className="mb-3">
                <div className="fw-semibold small mb-1 d-flex align-items-center gap-2">
                  Will update ({staged.validCount})
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0"
                    style={{ lineHeight: 1 }}
                    title="Download update list"
                    onClick={() =>
                      downloadValidatedRowsCsv(
                        staged.valid,
                        "bulk-import-to-update.csv"
                      )
                    }
                  >
                    <Download size={13} />
                  </button>
                </div>
                <div style={{ maxHeight: 200, overflowY: "auto" }}>
                  <Table
                    size="sm"
                    striped
                    bordered
                    style={{ fontSize: "0.78rem" }}
                  >
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Brand</th>
                        <th>Live</th>
                        <th>Current Coverage</th>
                        <th>New Coverage</th>
                        <th>Metadata</th>
                      </tr>
                    </thead>
                    <tbody>
                      {staged.valid.map((r) => (
                        <tr key={r.id}>
                          <td>
                            <code>{r.code}</code>
                          </td>
                          <td>{r.brandName}</td>
                          <td>
                            {r.isActive ? (
                              <Badge
                                bg="success"
                                style={{ fontSize: "0.65rem" }}
                              >
                                Live
                              </Badge>
                            ) : (
                              <Badge
                                bg="secondary"
                                style={{ fontSize: "0.65rem" }}
                              >
                                Inactive
                              </Badge>
                            )}
                          </td>
                          <td>
                            {COVERAGE_LABELS[r.currentCoverage] ??
                              r.currentCoverage}
                          </td>
                          <td>{COVERAGE_LABELS[r.coverage] ?? r.coverage}</td>
                          <td>{r.hasMetadata ? "✓" : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}

            <Form.Group className="mt-2">
              <Form.Label className="small fw-semibold">
                Comment (required)
              </Form.Label>
              <Form.Control
                size="sm"
                placeholder="Reason for bulk coverage update…"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={step === "confirming"}
              />
            </Form.Group>

            {errorMsg && (
              <Alert variant="danger" className="mt-2 mb-0 py-2 small">
                {errorMsg}
              </Alert>
            )}
          </>
        )}

        {step === "done" && result && (
          <Alert variant={result.errorCount === 0 ? "success" : "warning"}>
            {result.successCount} coupon(s) updated successfully.
            {result.errorCount > 0 && ` ${result.errorCount} failed.`}
            {result.errorCount > 0 && (
              <div className="mt-2">
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={downloadErrorCsv}
                >
                  Download Error CSV
                </Button>
              </div>
            )}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        {(step === "select" || step === "validating") && (
          <>
            <Button variant="secondary" size="sm" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleStage}
              disabled={!file || step === "validating"}
            >
              {step === "validating" ? (
                <Spinner size="sm" animation="border" />
              ) : (
                "Validate CSV"
              )}
            </Button>
          </>
        )}
        {(step === "staged" || step === "confirming") && (
          <>
            <Button variant="secondary" size="sm" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="success"
              size="sm"
              onClick={handleConfirm}
              disabled={
                !comment.trim() ||
                staged?.validCount === 0 ||
                step === "confirming"
              }
            >
              {step === "confirming" ? (
                <Spinner size="sm" animation="border" />
              ) : (
                `Confirm ${staged?.validCount ?? 0} update(s)`
              )}
            </Button>
          </>
        )}
        {step === "done" && (
          <Button variant="primary" size="sm" onClick={handleClose}>
            Close
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export const KindredCoupons: React.FC = () => {
  const { hasPreviousSearch, initialPage, rememberSearch, forgetSearch } =
    useListSearchMemory(listSearchKey.KINDRED_COUPONS);
  const [page, setPage] = useState(initialPage);
  const [limit] = useState(100);
  const [showImport, setShowImport] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadedRun, setUploadedRun] =
    useState<ICouponBulkUpdateRunSummary | null>(null);

  // Filters (persisted)
  const [couponCode, setCouponCode] = useSettings(
    settingsType.KINDRED_COUPONS_CODE
  );
  const [brandId, setBrandId] = useSettings(
    settingsType.KINDRED_COUPONS_BRAND_ID
  );
  const [topBrands, setTopBrands] = useSettings(
    settingsType.KINDRED_COUPONS_TOP_BRANDS
  );
  const [countryCode, setCountryCode] = useSettings(
    settingsType.KINDRED_COUPONS_COUNTRY
  );
  const [publishedStatus, setPublishedStatus] = useSettings(
    settingsType.KINDRED_COUPONS_PUBLISHED_STATUS
  );
  const [source, setSource] = useSettings(settingsType.KINDRED_COUPONS_SOURCE);
  const [domain, setDomain] = useSettings(settingsType.KINDRED_COUPONS_DOMAIN);
  const [domainMode, setDomainMode] = useSettings(
    settingsType.KINDRED_COUPONS_DOMAIN_MODE
  );
  const [validatedOnly, setValidatedOnly] = useSettings(
    settingsType.KINDRED_COUPONS_VALIDATED_ONLY
  );
  const [active, setActive] = useSettings(settingsType.KINDRED_COUPONS_ACTIVE);
  const [coverage, setCoverage] = useSettings(
    settingsType.KINDRED_COUPONS_COVERAGE
  );

  // Auth for direct API calls
  const { getToken, can, isAdmin } = useAuth();
  const canBulkUpdate =
    isAdmin ||
    can(PermissionAction.ReadAny, PermissionResource.CouponBulkUpdate);
  const canUploadCoupons =
    isAdmin ||
    can(PermissionAction.CreateAny, PermissionResource.CouponBulkUpdate);

  // Reference data (countries & networks only — brands use async search)
  const refCountries = useGet<string[]>("kindred/reference/countries", true);
  const refNetworks = useGet<string[]>("kindred/reference/networks", true);

  // Brand async search for typeahead
  const [selectedBrands, setSelectedBrands] = useState<
    readonly { value: string; label: string }[]
  >([]);

  // A link from another page (?code=&brandId=&country=) replaces the saved filters.
  const [searchParams, setSearchParams] = useSearchParams();
  const prefill = useRef(
    ["code", "brandId", "country"].some((k) => searchParams.get(k))
      ? {
          code: searchParams.get("code") ?? "",
          brandId: searchParams.get("brandId") ?? "",
          country: (searchParams.get("country") ?? "").toUpperCase(),
        }
      : null
  );
  const [prefillStage, setPrefillStage] = useState<"apply" | "search" | null>(
    prefill.current ? "apply" : null
  );

  // Resolve brandIds to labels whenever they change from outside the typeahead
  useEffect(() => {
    const ids = selectedBrands.map((b) => b.value).join(",");
    if (brandId && ids !== brandId) {
      let stale = false;
      (async () => {
        try {
          const token = await getToken();
          const ids = brandId.split(",").filter(Boolean);
          const resolved = await Promise.all(
            ids.map(async (id: string) => {
              try {
                const brand = await api.get<IKindredBrandReference>(
                  `kindred/reference/brands/${encodeURIComponent(id)}`,
                  {
                    headers: {
                      Authorization: token ? `Bearer ${token}` : undefined,
                    },
                  }
                );
                return brand
                  ? {
                      value: brand.id,
                      label: `${brand.name}${brand.isTopBrand ? " ★" : ""}`,
                    }
                  : null;
              } catch {
                return null;
              }
            })
          );
          if (stale) return;
          setSelectedBrands(
            resolved.filter(Boolean) as { value: string; label: string }[]
          );
        } catch {
          // brands not found — clear
        }
      })();
      return () => {
        stale = true;
      };
    }
  }, [brandId]);

  // Build query
  let queryParams = `page=${page}&limit=${limit}`;
  if (couponCode)
    queryParams += `&couponCode=${encodeURIComponent(couponCode)}`;
  if (brandId) queryParams += `&brandId=${encodeURIComponent(brandId)}`;
  if (topBrands) queryParams += `&topBrands=true`;
  if (countryCode)
    queryParams += `&countryCode=${encodeURIComponent(countryCode)}`;
  if (publishedStatus) queryParams += `&publishedStatus=${publishedStatus}`;
  if (source) queryParams += `&source=${encodeURIComponent(source)}`;
  if (domain) queryParams += `&domain=${encodeURIComponent(domain)}`;
  if (domainMode && domainMode !== "both")
    queryParams += `&domainSearchMode=${domainMode}`;
  if (validatedOnly) queryParams += `&validatedCampaignDomainsOnly=true`;
  if (active) queryParams += `&active=true`;
  if (coverage !== "") queryParams += `&coverage=${coverage}`;

  const results = useGet<IKindredCouponsResponse>(
    `kindred/coupons?${queryParams}`,
    false
  );

  const handleRefresh = useCallback(async () => {
    rememberSearch(page);
    await results.refetch();
  }, [results, rememberSearch, page]);

  // Re-run the last search on mount so returning from a detail page
  // restores the results, not just the filters.
  const { refetch } = results;
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current || !hasPreviousSearch || prefill.current) return;
    restoredRef.current = true;
    refetch().catch(() => undefined);
  }, [hasPreviousSearch, refetch]);

  // Only a real page change re-runs the search — the mount render and the
  // reset done by Clear Filters already match what was fetched.
  const lastFetchedPageRef = useRef(initialPage);
  useEffect(() => {
    if (lastFetchedPageRef.current === page) return;
    lastFetchedPageRef.current = page;
    handleRefresh();
  }, [page]);

  useEffect(() => {
    const p = prefill.current;
    if (!p) return;
    setCouponCode(p.code);
    setBrandId(p.brandId);
    setSelectedBrands([]);
    setTopBrands(false);
    setCountryCode(p.country);
    setPublishedStatus("");
    setSource("");
    setDomain("");
    setDomainMode("both");
    setValidatedOnly(false);
    setActive(false);
    setCoverage("");
    lastFetchedPageRef.current = 1;
    setPage(1);
    setPrefillStage("search");
    setSearchParams({}, { replace: true });
  }, []);

  // Runs a render later, once the request URL carries the prefilled filters.
  useEffect(() => {
    if (prefillStage !== "search") return;
    setPrefillStage(null);
    rememberSearch(1);
    refetch().catch(() => undefined);
  }, [prefillStage, refetch]);

  const handleClearFilters = () => {
    setCouponCode("");
    setBrandId("");
    setSelectedBrands([]);
    setTopBrands(false);
    setCountryCode("");
    setPublishedStatus("");
    setSource("");
    setDomain("");
    setDomainMode("both");
    setValidatedOnly(false);
    setActive(false);
    setCoverage("");
    forgetSearch();
    lastFetchedPageRef.current = 1;
    setPage(1);
  };

  const pagination = results.data?.pagination;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "—";
    try {
      return format(new Date(dateStr), "yyyy-MM-dd");
    } catch {
      return dateStr;
    }
  };

  return (
    <div>
      <PageHeader
        title="Kindred Live — Coupons"
        description="Search, create and bulk-load coupons in Kindred Live."
      />

      <PageToolbar>
        <ToolbarButton
          label="Create coupon"
          hint="Add a single coupon by hand."
          icon="bi-plus-lg"
          variant="success"
          to="/kindred/coupons/new"
        />
        <ToolbarButton
          label="Upload new coupons"
          hint={
            canUploadCoupons
              ? "Add many new coupons from a CSV in our template. The file is checked first and you see a summary before anything is created."
              : "Uploading coupons needs the bulk update permission (Lead QA Testers and above)."
          }
          icon="bi-upload"
          variant="primary"
          onClick={() => setShowUpload(true)}
          disabled={!canUploadCoupons}
        />
        <ToolbarButton
          label="Import coverage CSV"
          hint="Set coverage on existing coupons from a CSV of id, coverage."
          icon="bi-file-earmark-arrow-up"
          onClick={() => setShowImport(true)}
        />
        {canBulkUpdate && (
          <ToolbarButton
            label="Bulk updates"
            hint="History of bulk runs (uploads, coverage, deactivations) with their stored files and row outcomes."
            icon="bi-layers"
            to="/kindred/coupon-bulk-updates"
          />
        )}
      </PageToolbar>

      {uploadedRun && (
        <Alert variant="info" dismissible onClose={() => setUploadedRun(null)}>
          Queued {uploadedRun.queuedCount} new coupon
          {uploadedRun.queuedCount === 1 ? "" : "s"}. They are created in the
          background;{" "}
          <Link to="/kindred/coupon-bulk-updates">follow the run in Bulk updates</Link>.
        </Alert>
      )}

      <BulkImportModal show={showImport} onHide={() => setShowImport(false)} />
      <NewBulkUpdateModal
        show={showUpload}
        onHide={() => setShowUpload(false)}
        initialOperation={CouponBulkUpdateOperation.Create}
        lockOperation
        onStarted={(run) => {
          setShowUpload(false);
          setUploadedRun(run);
        }}
      />

      {/* Filters */}
      <Row className="mb-3 g-2">
        <Col md={3}>
          <Form.Label className="small">Coupon Code</Form.Label>
          <Form.Control
            size="sm"
            placeholder="Search coupon code..."
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Label className="small">Brand</Form.Label>
          <BrandMultiSelect
            value={selectedBrands}
            onChange={(opts) => {
              setBrandId(opts.map((o) => o.value).join(","));
              setSelectedBrands(opts);
            }}
          />
        </Col>
        <Col md={3}>
          <Form.Label className="small">Published Status</Form.Label>
          <Form.Select
            size="sm"
            value={publishedStatus}
            onChange={(e) => setPublishedStatus(e.target.value)}
          >
            {publishedStatusOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Label className="small">Source / Network</Form.Label>
          <Select
            isMulti
            options={(refNetworks.data || []).map((n) => ({
              value: n,
              label: n,
            }))}
            value={
              source
                ? source.split(",").map((s: string) => ({ value: s, label: s }))
                : []
            }
            onChange={(opts) =>
              setSource((opts || []).map((o) => o.value).join(","))
            }
            placeholder="All networks..."
            styles={{
              control: (base) => ({
                ...base,
                minHeight: 31,
                fontSize: "0.875rem",
              }),
            }}
          />
        </Col>
      </Row>

      <Row className="mb-3 g-2">
        <Col md={3}>
          <Form.Label className="small">Country</Form.Label>
          <Select
            isMulti
            options={(refCountries.data || []).map((c) => ({
              value: c,
              label: c,
            }))}
            value={
              countryCode
                ? countryCode
                    .split(",")
                    .map((c: string) => ({ value: c, label: c }))
                : []
            }
            onChange={(opts) =>
              setCountryCode((opts || []).map((o) => o.value).join(","))
            }
            placeholder="All countries..."
            styles={{
              control: (base) => ({
                ...base,
                minHeight: 31,
                fontSize: "0.875rem",
              }),
            }}
          />
        </Col>
        <Col md={3}>
          <Form.Label className="small">Domain</Form.Label>
          <Form.Control
            size="sm"
            placeholder="Search domains..."
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Label className="small">Domain Source</Form.Label>
          <Form.Select
            size="sm"
            value={domainMode}
            onChange={(e) => setDomainMode(e.target.value)}
          >
            {domainModeOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Label className="small">Coverage</Form.Label>
          <Form.Select
            size="sm"
            value={coverage}
            onChange={(e) => setCoverage(e.target.value)}
          >
            {coverageOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md="auto">
          <Form.Label className="small d-block">&nbsp;</Form.Label>
          <Form.Check
            inline
            type="checkbox"
            label="Top Brands"
            checked={topBrands}
            onChange={(e) => setTopBrands(e.target.checked)}
          />
        </Col>
        <Col md="auto">
          <Form.Label className="small d-block">&nbsp;</Form.Label>
          <Form.Check
            inline
            type="checkbox"
            label="Validated Campaign Domains Only"
            checked={validatedOnly}
            onChange={(e) => setValidatedOnly(e.target.checked)}
          />
        </Col>
        <Col md="auto">
          <Form.Label className="small d-block">&nbsp;</Form.Label>
          <Form.Check
            inline
            type="checkbox"
            label="Active Only"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <Button size="sm" variant="primary" onClick={handleRefresh}>
            {results.loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Search"
            )}
          </Button>{" "}
          <Button
            size="sm"
            variant="outline-primary"
            onClick={async () => {
              await handleRefresh();
            }}
          >
            Force Refresh
          </Button>{" "}
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={handleClearFilters}
          >
            Clear Filters
          </Button>
        </Col>
      </Row>

      {/* Results */}
      {results.error && (
        <div className="text-danger mb-3">Error: {results.error.message}</div>
      )}

      <div className="list-group list-group-flush">
        {results.loading && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" /> Loading...
          </div>
        )}
        {!results.loading && results.data?.results.length === 0 && (
          <div className="text-center text-muted py-3">
            No results. Apply filters and click Search.
          </div>
        )}
        {(results.data?.results || []).map((coupon) => (
          <div
            key={coupon.id}
            className={`list-group-item px-3 py-2${coupon.deactivatedAt ? " bg-danger bg-opacity-10 border-danger border-opacity-25" : ""}`}
            style={{ fontSize: "0.85rem" }}
          >
            {/* Row 1: primary info */}
            <div className="d-flex align-items-center flex-wrap gap-2">
              <Link
                to={`/kindred/coupons/${coupon.id}`}
                className="fw-semibold text-nowrap"
              >
                <code>{coupon.code || "—"}</code>
              </Link>
              {coupon.code && (
                <CopyButton text={coupon.code} title="Copy code" />
              )}
              <span className="text-muted">|</span>
              <span title={`Brand ID: ${coupon.brandId}`}>
                {coupon.brandName}
                {coupon.isTopBrand && (
                  <Badge
                    bg="info"
                    className="ms-1"
                    style={{ fontSize: "0.65rem" }}
                  >
                    ★
                  </Badge>
                )}
              </span>
              <CopyButton text={coupon.brandId} title="Copy Brand ID" />
              <span className="text-muted">|</span>
              {statusBadge(coupon.publishedStatus)}
              {coupon.deactivatedAt && (
                <Badge bg="dark" style={{ fontSize: "0.65rem" }}>
                  Deactivated
                </Badge>
              )}
              {coupon.coverage === 1 ? (
                <Badge bg="success" style={{ fontSize: "0.65rem" }}>
                  SiteWide
                </Badge>
              ) : (
                <Badge
                  bg="outline-secondary"
                  text="muted"
                  style={{ fontSize: "0.65rem", border: "1px solid #ccc" }}
                >
                  Not SiteWide
                </Badge>
              )}
              <span className="ms-auto d-flex align-items-center gap-2 text-nowrap">
                <small className="text-muted">
                  P{coupon.priority} · {coupon.source} · {coupon.discount}%
                </small>
                <small className="text-muted">
                  {formatDate(coupon.validFrom)} → {formatDate(coupon.validTo)}
                </small>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Copy JSON</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0"
                    style={{ lineHeight: 1 }}
                    onClick={() =>
                      navigator.clipboard.writeText(
                        JSON.stringify(coupon, null, 2)
                      )
                    }
                  >
                    <Code size={13} />
                  </button>
                </OverlayTrigger>
              </span>
            </div>
            {/* Row 2: summary, countries, domains */}
            <div
              className="d-flex flex-wrap gap-3 mt-1"
              style={{ fontSize: "0.78rem" }}
            >
              {coupon.summary && (
                <span>
                  <span className="text-muted fw-semibold">Summary: </span>
                  <span title={coupon.summary}>
                    {coupon.summary.length > 80
                      ? `${coupon.summary.substring(0, 80)}…`
                      : coupon.summary}
                  </span>
                </span>
              )}
              <span>
                <span className="text-muted fw-semibold">Countries: </span>
                {coupon.countries.length > 0 ? (
                  <span title={coupon.countries.join(", ")}>
                    {coupon.countries.length > 10
                      ? `${coupon.countries.slice(0, 10).join(", ")}… (${coupon.countries.length})`
                      : coupon.countries.join(", ")}
                  </span>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </span>
            </div>
            {/* Row 3: domains */}
            <div
              className="d-flex flex-wrap gap-3 mt-1"
              style={{ fontSize: "0.78rem" }}
            >
              <span>
                <span className="text-muted fw-semibold">Coupon Domains: </span>
                {coupon.couponDomains.length > 0 ? (
                  <span title={coupon.couponDomains.join(", ")}>
                    {coupon.couponDomains.length > 5
                      ? `${coupon.couponDomains.slice(0, 5).join(", ")}… (${coupon.couponDomains.length})`
                      : coupon.couponDomains.join(", ")}
                  </span>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </span>
              <span>
                <span className="text-muted fw-semibold">
                  Campaign Domains:{" "}
                </span>
                {coupon.campaignDomains.length > 0 ? (
                  <span title={coupon.campaignDomains.join(", ")}>
                    {coupon.campaignDomains.length > 5
                      ? `${coupon.campaignDomains.slice(0, 5).join(", ")}… (${coupon.campaignDomains.length})`
                      : coupon.campaignDomains.join(", ")}
                  </span>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <span className="small text-muted">
            Showing page {pagination.page} of {pagination.totalPages} (
            {pagination.total} total)
          </span>
          <div>
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page === 1}
              onClick={() => setPage(1)}
            >
              First
            </Button>{" "}
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </Button>{" "}
            <Form.Select
              size="sm"
              className="d-inline-block w-auto"
              value={page}
              onChange={(e) => setPage(Number(e.target.value))}
            >
              {Array.from({ length: pagination.totalPages }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              ))}
            </Form.Select>{" "}
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page === pagination.totalPages}
              onClick={() =>
                setPage((p) => Math.min(pagination.totalPages, p + 1))
              }
            >
              Next
            </Button>{" "}
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page === pagination.totalPages}
              onClick={() => setPage(pagination.totalPages)}
            >
              Last
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
